import React, { useState } from "react";
import { t } from "i18next";
import { postDecor } from "../../../api/post/postDecor";
import Modal from "../../../common/components/MessageModal";

function UploadBox({ label, hint, accept, preview, onChange, children }) {
    return (
        <div>
            <p className="text-sm font-medium text-gray-700 mb-3">{label}</p>
            <div className="flex items-start gap-4">
                {preview}
                <div className="flex flex-col gap-1.5">
                    {hint && <p className="text-xs text-gray-400 leading-relaxed">{hint}</p>}
                    <label className="inline-flex items-center gap-2 text-sm text-[#147F26] font-medium cursor-pointer hover:text-[#0f5f1a] transition">
                        <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4" stroke="currentColor" strokeWidth="2">
                            <path d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2M12 4v12M8 8l4-4 4 4" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        {t('upload') || t('add')}
                        <input
                            type="file"
                            accept={accept}
                            onChange={onChange}
                            className="hidden"
                        />
                    </label>
                    {children}
                </div>
            </div>
        </div>
    );
}

export default function DesignForm({ companyInfo }) {
    const [logo, setLogo] = useState(null);
    const [banner, setBanner] = useState(null);
    const [loading, setLoading] = useState(false);
    const [modal, setModal] = useState({ show: false, message: "" });

    const handleSubmit = async () => {
        if (!logo && !banner) return;
        setLoading(true);
        try {
            await postDecor({ logo, banner });
            setModal({
                show: true,
                message: banner ? t('bannerUploadedPendingModeration') : t('dataUpdateSuccess'),
            });
            window.location.reload();
        } catch (error) {
            console.error("Upload failed:", error);
            setModal({ show: true, message: t('updateError') });
        } finally {
            setLoading(false);
        }
    };

    const logoSrc = logo ? URL.createObjectURL(logo) : companyInfo?.decor?.logo;
    const bannerSrc = banner ? URL.createObjectURL(banner) : companyInfo?.decor?.banner;

    return (
        <div className="flex flex-col gap-7 max-w-xl">
            <h3 className="text-base font-semibold text-gray-800">{t('design') || t('cover')}</h3>

            {/* Logo */}
            <UploadBox
                label={t('logo')}
                hint={`${t('req_size')} ${t('req_size_num')}`}
                accept="image/*"
                onChange={(e) => setLogo(e.target.files?.[0])}
                preview={
                    <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center flex-shrink-0 border border-gray-200">
                        {logoSrc ? (
                            <img src={logoSrc} alt="logo" className="w-full h-full object-cover" />
                        ) : (
                            <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8 text-gray-300" stroke="currentColor" strokeWidth="1.2">
                                <rect x="3" y="3" width="18" height="18" rx="3" />
                                <circle cx="8.5" cy="8.5" r="1.5" />
                                <path d="M21 15l-5-5L5 21" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        )}
                    </div>
                }
            />

            <div className="border-t border-gray-100" />

            {/* Banner */}
            <UploadBox
                label={t('cover')}
                hint={`${t('req_size')} ${t('req_banner_size')}`}
                accept="image/*"
                onChange={(e) => setBanner(e.target.files?.[0])}
                preview={
                    <div className="w-32 h-20 rounded-xl overflow-hidden bg-gray-100 flex items-center justify-center flex-shrink-0 border border-gray-200">
                        {bannerSrc ? (
                            <img src={bannerSrc} alt="banner" className="w-full h-full object-cover" />
                        ) : (
                            <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8 text-gray-300" stroke="currentColor" strokeWidth="1.2">
                                <rect x="3" y="3" width="18" height="18" rx="3" />
                                <circle cx="8.5" cy="8.5" r="1.5" />
                                <path d="M21 15l-5-5L5 21" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        )}
                    </div>
                }
            >
                {(banner || companyInfo?.decor?.banner) && (
                    <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
                        {t('bannerStatusPending')}
                    </span>
                )}
            </UploadBox>

            <div>
                <button
                    onClick={handleSubmit}
                    className="bg-[#147F26] text-white py-2.5 px-6 rounded-xl font-medium hover:bg-[#0f5f1a] disabled:opacity-60 transition"
                    disabled={loading || (!logo && !banner)}
                >
                    {loading ? t('saving') : t('save')}
                </button>
            </div>

            {modal.show && (
                <Modal
                    message={modal.message}
                    onClose={() => setModal({ show: false, message: "" })}
                />
            )}
        </div>
    );
}
